/**
 * Pure resolvers for dual-field target-time model (ADR-0013).
 *
 * All functions are stateless and side-effect-free — safe to call in any
 * context (frontend, backend, tests). Tenant `fullTimeHoursPerWeek` is passed
 * as an argument; callers are responsible for fetching it from `core.settings`.
 */

/**
 * Resolve effective hours per day from the stored dual-field pair.
 *
 * @param workloadHoursPerDay - Non-null when period is in hours-mode.
 * @param workloadPercent - Non-null when period is in percent-mode.
 * @param fullTimeHoursPerWeek - Tenant full-time baseline.
 * @param workingDaysPerWeek - Working days per week (from workdayMask or strategy).
 */
export function resolveWorkloadHoursPerDay(
  workloadHoursPerDay: number | null,
  workloadPercent: number | null,
  fullTimeHoursPerWeek: number,
  workingDaysPerWeek: number
): number {
  if (workloadHoursPerDay !== null) return workloadHoursPerDay;
  if (workloadPercent !== null) {
    const fullTimeHoursPerDay = workingDaysPerWeek > 0 ? fullTimeHoursPerWeek / workingDaysPerWeek : 0;
    return (workloadPercent / 100) * fullTimeHoursPerDay;
  }
  return 0;
}

/**
 * Resolve effective workload percentage from the stored dual-field pair.
 * Result is rounded to two decimal places.
 *
 * @param workloadHoursPerDay - Non-null when period is in hours-mode.
 * @param workloadPercent - Non-null when period is in percent-mode.
 * @param fullTimeHoursPerWeek - Tenant full-time baseline.
 * @param workingDaysPerWeek - Working days per week (from workdayMask or strategy).
 */
export function resolveWorkloadPercent(
  workloadHoursPerDay: number | null,
  workloadPercent: number | null,
  fullTimeHoursPerWeek: number,
  workingDaysPerWeek: number
): number {
  if (workloadPercent !== null) return workloadPercent;
  if (workloadHoursPerDay !== null) {
    const fullTimeHoursPerDay = workingDaysPerWeek > 0 ? fullTimeHoursPerWeek / workingDaysPerWeek : 0;
    if (fullTimeHoursPerDay <= 0) return 0;
    return Math.round((workloadHoursPerDay / fullTimeHoursPerDay) * 100 * 100) / 100;
  }
  return 0;
}

/**
 * Resolve the effective weekly hours for a period.
 * Weekly hours = effectiveHoursPerDay × workingDays.
 */
export function resolveWeeklyHours(
  workloadHoursPerDay: number | null,
  workloadPercent: number | null,
  fullTimeHoursPerWeek: number,
  workingDaysPerWeek: number,
  workingDays: number
): number {
  const hoursPerDay = resolveWorkloadHoursPerDay(workloadHoursPerDay, workloadPercent, fullTimeHoursPerWeek, workingDaysPerWeek);
  return hoursPerDay * workingDays;
}
